// src/pages/accounts/Payables.jsx
// D-289 Phase 1c — Payables: every vendor and subcontractor's KHAATA.
//
// WHY THIS EXISTS
// Vendors and subcontractors are paid against their running account, not bill
// by bill ("₹5 lakh billed, ₹3 lakh paid, ₹2 lakh due"). This one screen:
//   1. lists everyone who is owed money (or holds an advance), biggest due first
//   2. opens a party's khaata — bills, payments, and what is still due
//   3. records a payment - the amount is TYPED (never pre-filled: a ₹2,50,000
//      payment was once saved as the full due ₹30,79,752.6, 22 Oct 2026),
//      shown in words, and confirmed before saving; optional TDS
// All maths is in src/utils/khaata.js; all writes go through
// src/services/paymentService.js. This page never writes Firestore itself.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadKhaatas } from "../../services/payablesService";
import { newPaymentId, recordPartyPayment, reversePartyPayment, PAYMENT_MODES } from "../../services/paymentService";
import { timelineOf, previewPartyPayment, billsForDisplay } from "../../utils/khaata";
import { rupeesInWords } from "../../utils/amountInWords";
import { canDoAccounts, canEnterBills } from "../../services/purchaseBillService";

const MODE_LABELS = {
  cash: "Cash", upi: "UPI", bank_transfer: "Bank Transfer",
  neft: "NEFT", rtgs: "RTGS", cheque: "Cheque",
};
const STATE_UI = {
  paid:   { label: "Paid",         cls: "bg-green-100 text-green-700" },
  partly: { label: "Partly paid",  cls: "bg-blue-100 text-blue-700" },
  due:    { label: "Due",          cls: "bg-amber-100 text-amber-700" },
};

const inr  = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const date = (ms) => ms
  ? new Date(ms).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
  : "-";

export default function Payables() {
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user.company_id || "";
  const navigate  = useNavigate();   // P3d-5b-2: "View bill" on a bill row

  const [parties,  setParties]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");
  const [filter,   setFilter]   = useState("all");   // "all" | "vendor" | "subcontractor"
  const [search,   setSearch]   = useState("");
  const [openKey,  setOpenKey]  = useState(null);    // party.key of the open khaata

  // Record Payment form
  const [pay,     setPay]     = useState(null);      // { party, payment_id }
  const [form,    setForm]    = useState({ amount: "", mode: "bank_transfer", reference_no: "", notes: "", withTds: false, tds: "" });
  const [saving,  setSaving]  = useState(false);
  // "Choose bills": null = oldest bill first (default); { bill_id: "amount" } = user picked bills
  const [chosen,   setChosen]   = useState(null);
  const [showPaid, setShowPaid] = useState(false);   // paid bills collapsed by default

  // D-315 Reverse a wrongly recorded payment. owner / accounts only (the
  // Firestore rule enforces it too); a payment can be reversed once.
  const canReverse = canDoAccounts(user); // D-314: owner / accounts or "Accounts access"
  const [rev,       setRev]       = useState(null);  // timeline entry of the payment being reversed
  const [revReason, setRevReason] = useState("");
  const [revSaving, setRevSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      setParties(await loadKhaatas(companyId));
    } catch (err) {
      console.error("Payables load error:", err);
      setError(err.message || "Failed to load payables");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = useMemo(() => parties.filter((p) => {
    if (filter !== "all" && p.party_type !== filter) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [parties, filter, search]);

  const totalDue     = parties.reduce((s, p) => s + p.due, 0);
  const totalAdvance = parties.reduce((s, p) => s + p.advance, 0);
  const open         = parties.find((p) => p.key === openKey) || null;

  // ── Record Payment ────────────────────────────────────────────────────
  // The payment id is created when the form OPENS and reused on every
  // Confirm click, so a double click can never record the payment twice.
  const openPay = (party) => {
    setForm({
      amount: "",   // typed every time - never pre-filled with the due
      mode: "bank_transfer", reference_no: "", notes: "", withTds: false, tds: "",
    });
    setChosen(null);
    setPay({ party, payment_id: newPaymentId() });
  };

  const gross   = Number(form.amount || 0);
  const tds     = form.withTds ? Number(form.tds || 0) : 0;
  const netCash = Math.max(gross - tds, 0);

  // Bills that can still take money, in the order money is applied.
  const dueBillsForPay = pay ? billsForDisplay(pay.party.bills).filter((b) => b.due > 0) : [];
  const dueById        = Object.fromEntries(dueBillsForPay.map((b) => [b.id, b.due]));

  // "Choose bills" lines → allocations for paymentService. Empty or zero
  // lines are ignored; a line is never more than that bill's due. Whatever
  // is not allocated settles the oldest bills first (khaata.js).
  const allocations = chosen
    ? Object.entries(chosen)
        .map(([bill_id, amt]) => ({ bill_id, amount: Math.min(Number(amt || 0), dueById[bill_id] ?? 0) }))
        .filter((a) => a.amount > 0)
    : [];
  const allocatedTotal = allocations.reduce((s, a) => s + a.amount, 0);
  const overAllocated  = allocatedTotal - gross > 0.005;

  // Same maths as the khaata itself, so the preview can never disagree with the result.
  const preview = pay ? previewPartyPayment(pay.party, { gross, allocations }) : null;

  const confirmPay = async () => {
    if (!pay) return;
    if (!(gross > 0))              { alert("Enter the amount."); return; }
    if (tds < 0 || tds >= gross)   { alert("TDS must be less than the amount."); return; }
    if (overAllocated)             { alert("Amounts for the chosen bills are more than the payment amount."); return; }
    // Last look before money is recorded - a payment row can never be edited
    // or deleted (D-289), so the amount, words, party and reference are shown.
    const sure = window.confirm(
      `Pay ${inr(gross)} to ${pay.party.name}?\n` +
      `(${rupeesInWords(gross)} only)\n\n` +
      `Mode: ${MODE_LABELS[form.mode] || form.mode}` +
      (form.reference_no.trim() ? `\nReference: ${form.reference_no.trim()}` : "") +
      (tds > 0 ? `\nTDS: ${inr(tds)} · cash paid ${inr(netCash)}` : "") +
      `\n\nA payment cannot be edited or deleted after it is saved.`
    );
    if (!sure) return;
    setSaving(true);
    try {
      const res = await recordPartyPayment({
        payment_id:   pay.payment_id,
        company_id:   companyId,
        party_type:   pay.party.party_type,
        party_id:     pay.party.party_id,
        party_name:   pay.party.name,
        gross_amount: gross,
        tds_amount:   tds,
        payment_mode: form.mode,
        reference_no: form.reference_no,
        notes:        form.notes,
        allocations,
        paid_by:      user.uid || user.id || "",
        paid_by_name: user.name || "",
      });
      if (res.already_recorded) alert("This payment was already recorded.");
      setPay(null);
      await load();
    } catch (err) {
      alert(err.message || "Failed to record payment");
    } finally {
      setSaving(false);
    }
  };

  // ── Reverse Payment (D-315) ───────────────────────────────────────────
  // Never edits the payment: paymentService writes ONE reversal row
  // ("prev_" + payment id). The khaata then drops the payment.
  const openReverse = (t) => { setRevReason(""); setRev(t); };

  const confirmReverse = async () => {
    if (!rev || !open) return;
    const why = revReason.trim();
    if (!why) { alert("Enter the reason for reversing."); return; }
    const sure = window.confirm(
      `Reverse the payment of ${inr(rev.gross)} to ${open.name}?\n` +
      `(${rupeesInWords(rev.gross)} only)\n` +
      `Paid on ${date(rev.at)}${rev.reference_no ? ` · Ref ${rev.reference_no}` : ""}\n\n` +
      `Reason: ${why}\n\n` +
      `Its bills go back to Due. The payment stays in the history marked "Reversed".\n` +
      `A reversal cannot be undone - record the correct payment afterwards.`
    );
    if (!sure) return;
    setRevSaving(true);
    try {
      const res = await reversePartyPayment({
        company_id:      companyId,
        payment_id:      rev.id,
        reason:          why,
        created_by:      user.uid || user.id || "",
        created_by_name: user.name || "",
      });
      if (res.already_reversed) alert("This payment was already reversed.");
      setRev(null);
      await load();
    } catch (err) {
      alert(err.message || "Failed to reverse the payment");
    } finally {
      setRevSaving(false);
    }
  };

  // ── RENDER ────────────────────────────────────────────────────────────
  return (
    <div className="p-4 pb-24 bg-gray-50 min-h-screen max-w-3xl mx-auto">

      {/* ═════════ KHAATA (one party) ═════════ */}
      {open ? (
        <div>
          <button onClick={() => setOpenKey(null)} className="text-sm text-slate-500 hover:text-slate-800 mb-3">
            ← All payables
          </button>

          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 mb-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase text-slate-400">
                  {open.party_type === "vendor" ? "Vendor" : "Subcontractor"}
                </p>
                <h1 className="text-xl font-bold text-slate-800">{open.name}</h1>
              </div>
              <div className="text-right">
                {open.advance > 0 ? (
                  <>
                    <p className="text-xs text-slate-400">Advance</p>
                    <p className="text-2xl font-black text-blue-600">{inr(open.advance)}</p>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-slate-400">Due</p>
                    <p className={`text-2xl font-black ${open.due > 0 ? "text-red-600" : "text-green-600"}`}>{inr(open.due)}</p>
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-4 text-center">
              <div className="bg-slate-50 rounded-xl p-2">
                <p className="text-[11px] text-slate-400">Billed</p>
                <p className="text-sm font-bold text-slate-700">{inr(open.billed)}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-2">
                <p className="text-[11px] text-slate-400">Paid</p>
                <p className="text-sm font-bold text-slate-700">{inr(open.paid_cash)}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-2">
                <p className="text-[11px] text-slate-400">TDS</p>
                <p className="text-sm font-bold text-slate-700">{inr(open.tds_total)}</p>
              </div>
            </div>

            {open.is_unassigned ? (
              <p className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3">
                These bills are not linked to any vendor, so they cannot be paid here.
                Link the vendor on the purchase order to move them into the right khaata.
              </p>
            ) : (
              <button onClick={() => openPay(open)}
                className="w-full mt-4 py-3 rounded-xl bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700">
                💳 Record Payment
              </button>
            )}
          </div>

          {/* Bills with their status */}
          <h2 className="text-sm font-bold text-slate-600 mb-2">Bills</h2>
          <div className="space-y-2 mb-5">
            {open.bills.length === 0 && <p className="text-sm text-slate-400">No bills yet.</p>}
            {billsForDisplay(open.bills).filter((b) => b.state !== "paid" || showPaid).map((b) => (
              <div key={b.id} className="bg-white rounded-xl border border-slate-100 p-3 flex justify-between items-center">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-slate-800">{b.ref || "Bill"}</p>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATE_UI[b.state].cls}`}>
                      {STATE_UI[b.state].label}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {date(b.at)}{b.project_name ? ` · ${b.project_name}` : ""}
                    {b.bill_type === "proforma" && <span className="text-amber-700"> · tax invoice pending - no GST credit yet</span>}
                    {b.purchase_bill_id && canEnterBills(user) && (
                      <> · <button type="button" onClick={() => navigate(`/purchase-bills/${b.purchase_bill_id}`)} className="font-semibold text-blue-600 hover:text-blue-800">View bill</button></>
                    )}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-slate-800">{inr(b.amount)}</p>
                  {b.state === "partly" && <p className="text-[11px] text-amber-600">{inr(b.due)} due</p>}
                </div>
              </div>
            ))}
          </div>

          {open.bills.some((b) => b.state === "paid") && (
            <button onClick={() => setShowPaid((s) => !s)}
              className="w-full -mt-3 mb-5 text-xs font-semibold text-slate-500 bg-white border border-dashed border-slate-200 rounded-xl py-2 hover:text-slate-800">
              {showPaid ? "Hide paid bills" : `Show paid bills (${open.bills.filter((b) => b.state === "paid").length})`}
            </button>
          )}

          {/* Full timeline */}
          <h2 className="text-sm font-bold text-slate-600 mb-2">History</h2>
          <div className="bg-white rounded-xl border border-slate-100 divide-y divide-slate-100">
            {timelineOf(open).map((t) => (
              <div key={`${t.kind}_${t.id}`} className="p-3 flex justify-between items-center">
                <div>
                  <p className="text-sm text-slate-700">
                    {t.kind === "bill"
                      ? (t.opening ? "Opening balance (due)" : `Bill — ${t.ref || ""}`)
                      : t.opening
                        ? "Opening balance (advance)"
                        : `Payment${t.mode ? ` — ${MODE_LABELS[t.mode] || t.mode}` : ""}${t.reference_no ? ` · ${t.reference_no}` : ""}`}
                  </p>
                  <p className="text-xs text-slate-400">
                    {date(t.at)}
                    {t.kind === "payment" && t.by ? ` · by ${t.by}` : ""}
                    {t.kind === "payment" && t.tds > 0 ? ` · TDS ${inr(t.tds)}` : ""}
                    {t.kind === "payment" && t.legacy ? " · recorded before khaata" : ""}
                    {t.opening && t.notes ? ` · ${t.notes}` : ""}
                  </p>
                  {t.reversed && (
                    <p className="text-xs font-semibold text-red-600 mt-0.5">
                      Reversed{t.reversed.at ? ` ${date(t.reversed.at)}` : ""} · {t.reversed.reason}{t.reversed.by ? ` · by ${t.reversed.by}` : ""}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className={`text-sm font-bold ${t.kind === "bill" ? "text-slate-800" : t.reversed ? "text-slate-400 line-through" : "text-green-600"}`}>
                    {t.kind === "bill" ? `+${inr(t.amount)}` : `−${inr(t.gross)}`}
                  </p>
                  {canReverse && t.kind === "payment" && !t.legacy && !t.opening && !t.reversed && (
                    <button type="button" onClick={() => openReverse(t)}
                      className="text-[11px] font-semibold text-red-600 hover:text-red-800 mt-0.5">
                      Reverse
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (

      /* ═════════ LIST (all parties) ═════════ */
      <div>
        <h1 className="text-xl font-bold text-slate-800 mb-4">Payables</h1>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-red-600 text-white p-4 rounded-2xl shadow-sm">
            <p className="text-xs opacity-80">Total Due</p>
            <p className="text-2xl font-black mt-0.5">{inr(totalDue)}</p>
          </div>
          <div className="bg-blue-600 text-white p-4 rounded-2xl shadow-sm">
            <p className="text-xs opacity-80">Advance Given</p>
            <p className="text-2xl font-black mt-0.5">{inr(totalAdvance)}</p>
          </div>
        </div>

        <div className="flex gap-2 mb-3 flex-wrap">
          {[
            { key: "all",           label: "All" },
            { key: "vendor",        label: "Vendors" },
            { key: "subcontractor", label: "Subcontractors" },
          ].map((t) => (
            <button key={t.key} onClick={() => setFilter(t.key)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold ${
                filter === t.key ? "bg-slate-800 text-white" : "bg-white text-slate-600 border border-slate-200"
              }`}>
              {t.label}
            </button>
          ))}
          <input placeholder="Search name..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="flex-1 min-w-[140px] border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none bg-white" />
        </div>

        {loading ? (
          <p className="text-slate-400 text-sm text-center mt-10">Loading...</p>
        ) : error ? (
          <p className="text-red-600 text-sm text-center mt-10">{error}</p>
        ) : visible.length === 0 ? (
          <div className="text-center mt-12">
            <div className="text-4xl mb-3">✅</div>
            <p className="text-slate-500 font-medium">Nothing to show</p>
          </div>
        ) : (
          <div className="space-y-2">
            {visible.map((p) => (
              <button key={p.key} onClick={() => { setOpenKey(p.key); setShowPaid(false); }}
                className="w-full text-left bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex justify-between items-center hover:border-slate-300">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-slate-800">{p.name}</p>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                      {p.party_type === "vendor" ? "Vendor" : "Subcontractor"}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Billed {inr(p.billed)} · Paid {inr(p.paid_cash)}
                  </p>
                </div>
                <div className="text-right">
                  {p.advance > 0 ? (
                    <>
                      <p className="text-lg font-black text-blue-600">{inr(p.advance)}</p>
                      <p className="text-[11px] text-slate-400">Advance</p>
                    </>
                  ) : (
                    <>
                      <p className={`text-lg font-black ${p.due > 0 ? "text-red-600" : "text-green-600"}`}>{inr(p.due)}</p>
                      <p className="text-[11px] text-slate-400">{p.due > 0 ? "Due" : "Settled"}</p>
                    </>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      )}

      {/* ═════════ REVERSE PAYMENT (D-315) ═════════ */}
      {rev && open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-slate-800 text-lg">Reverse payment</h3>
              <button onClick={() => setRev(null)} className="text-slate-400 hover:text-slate-700 text-xl">✕</button>
            </div>

            <div className="bg-red-50 border border-red-100 rounded-xl p-3 mb-4 text-sm">
              <p className="font-semibold text-slate-800">{open.name}</p>
              <p className="text-slate-700 mt-1">
                {inr(rev.gross)} · {MODE_LABELS[rev.mode] || rev.mode || "-"} · {date(rev.at)}
              </p>
              <p className="text-xs text-slate-600 mt-0.5">{rupeesInWords(rev.gross)} only</p>
              {rev.reference_no && <p className="text-xs text-slate-500 mt-0.5">Reference: {rev.reference_no}</p>}
              {rev.tds > 0 && <p className="text-xs text-slate-500 mt-0.5">TDS {inr(rev.tds)} · cash paid {inr(rev.net)}</p>}
            </div>

            <p className="text-xs text-slate-500 mb-3">
              Use this only when the payment was recorded wrongly. It stays in the history marked
              "Reversed", its bills go back to Due, and the Cash / Bank Book shows it cancelled.
              Then record the correct payment.
            </p>

            <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Reason (required)</label>
            <textarea rows={2} maxLength={500} autoFocus value={revReason}
              onChange={(e) => setRevReason(e.target.value)}
              placeholder="e.g. Amount typed wrong - paid ₹2,50,000, not the full due"
              className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none" />

            <div className="flex gap-3 mt-5">
              <button onClick={() => setRev(null)}
                className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-600 font-semibold text-sm">
                Cancel
              </button>
              <button onClick={confirmReverse} disabled={revSaving || !revReason.trim()}
                className="flex-1 py-3 rounded-xl bg-red-600 text-white font-semibold text-sm disabled:opacity-40 hover:bg-red-700">
                {revSaving ? "Reversing..." : `Reverse ${inr(rev.gross)}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═════════ RECORD PAYMENT ═════════ */}
      {pay && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-slate-800 text-lg">💳 Record Payment</h3>
              <button onClick={() => setPay(null)} className="text-slate-400 hover:text-slate-700 text-xl">✕</button>
            </div>

            <div className="bg-slate-50 rounded-xl p-3 mb-4">
              <p className="font-semibold text-slate-800">{pay.party.name}</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {pay.party.advance > 0 ? `Advance ${inr(pay.party.advance)}` : `Due ${inr(pay.party.due)}`}
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-slate-500 uppercase block">Amount (₹)</label>
                  {pay.party.due > 0 && (
                    <button type="button" onClick={() => setForm((f) => ({ ...f, amount: String(pay.party.due) }))}
                      className="text-xs font-semibold text-blue-600 hover:text-blue-800">
                      Fill full due {inr(pay.party.due)}
                    </button>
                  )}
                </div>
                <input type="number" min="0" inputMode="decimal" autoFocus value={form.amount}
                  onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                  placeholder="Type the amount paid"
                  className="w-full border border-slate-200 rounded-xl p-3 text-lg font-bold outline-none" />
                {gross > 0 && (
                  <p className="text-xs text-slate-600 mt-1">{rupeesInWords(gross)} only</p>
                )}
                {gross > pay.party.due && pay.party.due >= 0 && (
                  <p className="text-[11px] text-blue-600 mt-1">
                    {inr(gross - pay.party.due)} more than due — will show as advance.
                  </p>
                )}
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Payment Mode</label>
                <select value={form.mode} onChange={(e) => setForm((f) => ({ ...f, mode: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none bg-white">
                  {PAYMENT_MODES.map((m) => <option key={m} value={m}>{MODE_LABELS[m] || m}</option>)}
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Reference (optional)</label>
                <input placeholder="UTR, cheque no..." value={form.reference_no}
                  onChange={(e) => setForm((f) => ({ ...f, reference_no: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none" />
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={form.withTds}
                  onChange={(e) => setForm((f) => ({ ...f, withTds: e.target.checked, tds: "" }))} />
                TDS deducted
              </label>
              {form.withTds && (
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">TDS amount (₹)</label>
                  <input type="number" min="0" value={form.tds}
                    onChange={(e) => setForm((f) => ({ ...f, tds: e.target.value }))}
                    className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none" />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Cash paid: <b>{inr(netCash)}</b> · Khaata reduces by {inr(gross)}
                  </p>
                </div>
              )}

              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Note (optional)</label>
                <input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none" />
              </div>
            </div>

            {/* How this payment is applied — preview + optional "Choose bills" */}
            <div className="mt-4 border border-slate-200 rounded-xl p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-slate-500 uppercase">This payment will settle</p>
                {dueBillsForPay.length > 0 && (
                  <button type="button" onClick={() => setChosen((c) => (c ? null : {}))}
                    className="text-xs font-semibold text-blue-600 hover:text-blue-800">
                    {chosen ? "Use oldest first" : "Choose bills"}
                  </button>
                )}
              </div>

              {chosen && (
                <div className="space-y-1.5 mb-3">
                  <p className="text-[11px] text-slate-500">
                    Tick the bills the vendor named. Any amount left goes to the oldest bills.
                  </p>
                  {dueBillsForPay.map((b) => {
                    const ticked = b.id in chosen;
                    const tooMuch = ticked && Number(chosen[b.id] || 0) > b.due;
                    return (
                      <div key={b.id} className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={ticked}
                          onChange={(e) => setChosen((c) => {
                            const next = { ...c };
                            if (e.target.checked) next[b.id] = String(b.due); else delete next[b.id];
                            return next;
                          })} />
                        <span className="flex-1 truncate text-slate-700">{b.ref || "Bill"} · {date(b.at)}</span>
                        {ticked ? (
                          <input type="number" min="0" value={chosen[b.id]}
                            onChange={(e) => setChosen((c) => ({ ...c, [b.id]: e.target.value }))}
                            title={`Max ${inr(b.due)}`}
                            className={`w-28 border rounded-lg px-2 py-1 text-right text-sm outline-none ${tooMuch ? "border-red-400" : "border-slate-200"}`} />
                        ) : (
                          <span className="w-28 text-right text-xs text-slate-400">{inr(b.due)} due</span>
                        )}
                      </div>
                    );
                  })}
                  {overAllocated && (
                    <p className="text-[11px] text-red-600">
                      Chosen bills add up to {inr(allocatedTotal)} — more than the payment amount.
                    </p>
                  )}
                </div>
              )}

              {preview && preview.lines.length > 0 ? (
                <div className="space-y-1">
                  {preview.lines.map((l) => (
                    <div key={l.bill_id} className="flex justify-between gap-2 text-xs">
                      <span className="text-slate-600 truncate">{l.ref || "Bill"} · {date(l.at)}</span>
                      <span className={`whitespace-nowrap font-semibold ${l.state_after === "paid" ? "text-green-700" : "text-blue-700"}`}>
                        {inr(l.applied_now)} {l.state_after === "paid" ? "· fully paid" : `· ${inr(l.due_after)} left`}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400">
                  {gross > 0 ? "No bill is due — the whole amount will be an advance." : "Enter the amount to see which bills it settles."}
                </p>
              )}
              {preview && gross > 0 && preview.advance_after > 0 && (
                <p className="text-[11px] text-blue-600 mt-1">Advance after this payment: {inr(preview.advance_after)}</p>
              )}
            </div>

            <div className="flex gap-3 mt-5">
              <button onClick={() => setPay(null)}
                className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-600 font-semibold text-sm">
                Cancel
              </button>
              <button onClick={confirmPay} disabled={saving || overAllocated}
                className="flex-1 py-3 rounded-xl bg-blue-600 text-white font-semibold text-sm disabled:opacity-40 hover:bg-blue-700">
                {saving ? "Saving..." : `Pay ${inr(netCash)}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
